if(!customElements.get('lsg-hero')) {
    class LSGHero extends HTMLElement {
        constructor() {
            super();
            this.classes = {
                rotator: 'js-lsg-hero-image-rotator',
                image: 'js-lsg-hero-rotating-image',
                opacity: {
                    visible: 'opacity-100',
                    hidden: 'opacity-0'
                }
            }

            this.elements = {
                rotator: this.querySelector(`.${this.classes.rotator}`),
                images: this.querySelectorAll(`.${this.classes.image}`)
            }

            this.currentIndex = 0;
            this.rotationInterval = null;
            this.rotationTime = null;
        }

        /**
         * Called when the element is added to the DOM
         * Initializes the component and sets up image rotation
         */
        connectedCallback() {
            if(this.checkIfEmpty()) return;
            this.init();
        }

        /**
         * Called when the element is removed from the DOM
         * Cleans up the rotation interval
         */
        disconnectedCallback() {
            this.cleanup();
        }

        /**
         * Checks if the component has images to rotate
         * @returns {boolean} True if empty, false if has content
         */
        checkIfEmpty() {
            return !this.elements.rotator || !this.elements.images.length || this.elements.images.length <= 1;
        }

        /**
         * Main initialization method
         * Sets up image rotation if conditions are met
         */
        init() {
            if(!this.shouldInit()) return;
            this.setupRotation();
        }

        /**
         * Determines if the image rotation should be initialized
         * @returns {boolean} True if rotation should initialize, false otherwise
         */
        shouldInit() {
            return this.elements.rotator && this.elements.images.length > 1;
        }

        /**
         * Sets up the image rotation functionality
         * Gets rotation time from data attribute and starts the rotation
         */
        setupRotation() {
            this.rotationTime = this.getRotationTime();
            this.startRotation();
        }

        /**
         * Gets the rotation time from data attribute with validation
         * @returns {number} Rotation time in milliseconds
         */
        getRotationTime() {
            const rotationTimeAttr = this.elements.rotator?.dataset.rotationTime;
            const time = rotationTimeAttr ? parseInt(rotationTimeAttr, 10) : 3000;
            return Math.max(1000, time); // Minimum 1 second
        }

        /**
         * Starts the image rotation interval
         */
        startRotation() {
            this.rotationInterval = setInterval(() => {
                this.rotateToNextImage();
            }, this.rotationTime);
        }

        /**
         * Rotates to the next image in the sequence
         * Handles smooth transitions between images
         */
        rotateToNextImage() {
            if (!this.elements.images.length) return;

            this.hideImage(this.currentIndex);
            this.currentIndex = this.getNextIndex();
            this.showImage(this.currentIndex);
        }

        /**
         * Gets the next image index (loops back to 0 if at end)
         * @returns {number} Next image index
         */
        getNextIndex() {
            return (this.currentIndex + 1) % this.elements.images.length;
        }

        /**
         * Shows an image by index
         * @param {number} index - Image index to show
         */
        showImage(index) {
            const image = this.elements.images[index];
            if (image) {
                image.classList.remove(this.classes.opacity.hidden);
                image.classList.add(this.classes.opacity.visible);
            }
        }

        /**
         * Hides an image by index
         * @param {number} index - Image index to hide
         */
        hideImage(index) {
            const image = this.elements.images[index];
            if (image) {
                image.classList.remove(this.classes.opacity.visible);
                image.classList.add(this.classes.opacity.hidden);
            }
        }

        /**
         * Pauses the image rotation
         * Useful for hover states or user interaction
         */
        pauseRotation() {
            if (this.rotationInterval) {
                clearInterval(this.rotationInterval);
                this.rotationInterval = null;
            }
        }

        /**
         * Resumes the image rotation
         */
        resumeRotation() {
            if (!this.rotationInterval) {
                this.startRotation();
            }
        }

        /**
         * Manually rotates to a specific image index
         * @param {number} index - The index of the image to show
         */
        goToImage(index) {
            if (!this.isValidIndex(index)) return;

            this.hideImage(this.currentIndex);
            this.currentIndex = index;
            this.showImage(this.currentIndex);
        }

        /**
         * Validates if an index is within the valid range
         * @param {number} index - Index to validate
         * @returns {boolean} True if valid, false otherwise
         */
        isValidIndex(index) {
            return index >= 0 && index < this.elements.images.length;
        }

        /**
         * Gets the current image index
         * @returns {number} Current image index
         */
        getCurrentImageIndex() {
            return this.currentIndex;
        }

        /**
         * Gets the total number of images
         * @returns {number} Total number of images
         */
        getTotalImages() {
            return this.elements.images.length;
        }

        /**
         * Updates the rotation time and restarts rotation
         * @param {number} newTime - New rotation time in milliseconds
         */
        updateRotationTime(newTime) {
            this.rotationTime = Math.max(1000, newTime);
            this.cleanup();
            this.startRotation();
        }

        /**
         * Cleans up the rotation interval
         */
        cleanup() {
            if (this.rotationInterval) {
                clearInterval(this.rotationInterval);
                this.rotationInterval = null;
            }
        }
    }

    customElements.define('lsg-hero', LSGHero);
}
